'use client';
import { useContext, useMemo } from 'react';
import useSWR, { KeyedMutator } from 'swr';
import { apiClient } from '~/lib/apiClient';
import { isArcApiError } from '~/src/services/errors';

import { isActiveToken } from '~/src/services/users';
import { RootContext } from '~/store';
import type { ArcApiError, PlayTicket } from '~/types/resources';

type UsePlayTickets = {
  playTickets?: PlayTicket[];
  errorMessage: ArcApiError | Error | undefined;
  mutate: KeyedMutator<{ play_tickets: PlayTicket[] } | ArcApiError>;
  isLoading: boolean;
  filterPlayTicketsByGachaId: (gachaId: string) => PlayTicket[];
};

export const usePlayTickets = (): UsePlayTickets => {
  const { token } = useContext(RootContext);
  const { data, error, mutate } = useSWR(
    isActiveToken(token) ? [token] : null,
    () =>
      apiClient.v1.play_tickets.$get({
        headers: {
          Authorization: `Bearer ${token?.token}`,
        },
      })
  );
  const playTickets = useMemo(() => {
    if (data == undefined) return [];
    if (isArcApiError(data)) return [];
    return data.play_tickets;
  }, [data]);
  const apiError: ArcApiError | Error | undefined = useMemo(() => {
    if (data != undefined && isArcApiError(data)) return data;
    return error;
  }, [data, error]);

  const filterPlayTicketsByGachaId = (gachaId: string): PlayTicket[] => {
    if (data == undefined) return [];
    if (apiError != undefined) return [];
    if (isArcApiError(data)) return [];
    return (data as { play_tickets: PlayTicket[] }).play_tickets.filter(
      (playTicket) => {
        return playTicket.gacha_restrictions.some(
          (gachaRestriction) => gachaRestriction.id === gachaId
        );
      }
    );
  };

  return {
    playTickets: playTickets,
    errorMessage: apiError,
    mutate,
    filterPlayTicketsByGachaId,
    isLoading: !data && !error,
  };
};
